<template>
  <section
    v-if="!autoDistribution"
    class="flex flex-col w-full p-8 md:p-12 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen"
  >
    <div class="flex justify-end max-w-6xl mx-auto w-full mb-8">
      <RouterLink
        to="/home"
        class="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-300 font-medium flex items-center space-x-2 text-sm shadow-md hover:shadow-lg"
      >
        <i class="fas fa-qrcode"></i>
        <span>Generate</span>
      </RouterLink>
    </div>
    <div class="max-w-6xl mx-auto w-full">
      <div class="text-center mb-8">
        <h1 class="text-4xl font-bold text-gray-800 mb-3">
          API Keys Configuration
        </h1>
        <p class="text-gray-600 text-lg">
          Manage your API keys for simplified usage
        </p>
      </div>

      <div class="flex justify-end mb-4">
        <button
          @click="toggleForm"
          class="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-300 font-medium flex items-center space-x-2 text-sm shadow-md hover:shadow-lg"
        >
          <i :class="showForm ? 'fas fa-minus' : 'fas fa-plus'"></i>
          <span>{{ showForm ? 'Hide form' : 'Add API Key' }}</span>
        </button>
      </div>

      <div
        v-if="showForm"
        class="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 transform transition-all duration-300 hover:shadow-2xl mb-8"
      >
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <div class="space-y-3">
            <label class="block text-base font-semibold text-gray-700"
              >Badge Name</label
            >
            <input
              type="text"
              v-model="formData.badgeName"
              class="mt-1 block w-full h-12 px-4 py-2 rounded-xl border-2 border-gray-200 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition duration-200 text-base placeholder-gray-400"
              required
              placeholder="Enter a name to identify this key"
            />
          </div>

          <div class="space-y-3">
            <label class="block text-base font-semibold text-gray-700"
              >API Key</label
            >
            <input
              type="password"
              v-model="formData.apiKey"
              class="mt-1 block w-full h-12 px-4 py-2 rounded-xl border-2 border-gray-200 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition duration-200 text-base placeholder-gray-400"
              required
              placeholder="Enter your API key"
            />
          </div>

          <div class="flex justify-end pt-6">
            <button
              type="submit"
              class="px-8 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all duration-300 font-medium flex items-center space-x-2 text-base shadow-md hover:shadow-lg"
            >
              <i class="fas fa-save"></i>
              <span>Save</span>
            </button>
          </div>
        </form>
      </div>

      <div class="mt-8">
        <h2 class="text-2xl font-bold text-gray-800 mb-4">
          Configured API Keys
        </h2>
        <div
          class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform transition-all duration-300 hover:shadow-2xl"
        >
          <table class="min-w-full">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-1/3"
                >
                  Badge Name
                </th>
                <th
                  class="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-1/3"
                >
                  Image preview
                </th>
                <th
                  class="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider w-1/3"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="(apiKey, index) in savedApiKeys"
                :key="index"
                class="hover:bg-gray-50 transition-colors duration-200"
              >
                <td
                  class="px-6 py-4 whitespace-nowrap text-gray-700 align-middle"
                >
                  {{ apiKey.badgeName }}
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-gray-700 align-middle"
                >
                  <img
                    :src="apiKey?.data?.image"
                    alt="Image preview"
                    class="w-10 h-10 object-cover rounded-md"
                  />
                </td>
                <td class="px-6 py-4 whitespace-nowrap align-middle">
                  <div class="flex items-center space-x-4">
                    <button
                      @click="deleteApiKey(index)"
                      class="text-red-600 hover:text-red-800 font-medium transition-colors duration-200 flex items-center space-x-2"
                    >
                      <i class="fas fa-trash-alt"></i>
                      <span>Delete</span>
                    </button>

                    <RouterLink
                      :to="`/distribution/?badge=${encodeURIComponent(
                        apiKey.apiKey
                      )}`"
                      class="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 flex items-center space-x-2"
                    >
                      <i class="fas fa-share-alt"></i>
                      <span>Auto Distribution</span>
                    </RouterLink>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, inject } from 'vue';
import Cookies from 'js-cookie';
import { RouterLink } from 'vue-router';

export default {
  setup() {
    const utils = inject('utils');
    const api = inject('api');
    const formData = ref({
      badgeName: '',
      apiKey: '',
    });
    const vpDetail = ref(null);

    const autoDistribution = ref(false);

    const savedApiKeys = ref([]);
    const showForm = ref(false);

    const toggleForm = () => {
      showForm.value = !showForm.value;
    };

    const loadApiKeys = () => {
      const savedKeys = Cookies.get('apiKeys');
      if (savedKeys) {
        savedApiKeys.value = JSON.parse(savedKeys);
      }
    };

    const saveApiKeys = () => {
      Cookies.set('apiKeys', JSON.stringify(savedApiKeys.value), {
        expires: 365,
      });
    };

    const handleSubmit = () => {
      console.log('formData', formData.value);
      const badgeBot = new utils.BadgeBot(formData.value.apiKey);
      badgeBot.getTemplate().then((template) => {
        console.log('template', template);
        if (template) {
          badgeBot
            .getIssuerDetailsLinkFromCode(template.issuerDID)
            .then((res) => {
              console.log('res', res);
              const profileIpfsUrl = res.service[0]?.serviceEndpoint;
              profileIpfsUrl &&
                badgeBot
                  .getJsonDataFromUrl(profileIpfsUrl)
                  .then((issuerProfile) => {
                    savedApiKeys.value.push({
                      badgeName: formData.value.badgeName,
                      apiKey: formData.value.apiKey,
                      data: {
                        ...template,
                        issuer: {
                          ...issuerProfile,
                        },
                      },
                      generations: [],
                    });
                    saveApiKeys();
                    formData.value = {
                      badgeName: '',
                      apiKey: '',
                    };
                  });
            });
        }
      });
    };

    const deleteApiKey = (index) => {
      savedApiKeys.value.splice(index, 1);
      saveApiKeys();
    };

    onMounted(() => {
      loadApiKeys();
      // remove autoLink from Cookies
      //Cookies.remove('autoLink');
    });

    return {
      formData,
      savedApiKeys,
      handleSubmit,
      deleteApiKey,
      showForm,
      toggleForm,
      autoDistribution,
    };
  },
};
</script>

<style scoped>
input:focus,
select:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
</style>
